import React, { useRef, useState } from 'react';
import MakeInStudioLink from '@/components/creation-flow/MakeInStudioLink';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ImageIcon, Loader2, Upload, FolderOpen, Trash2, Video } from 'lucide-react';
import { getProductFileUploadUrl, type MuxPreview } from '@/services/creatorApi';
import CategoryTagsFields from '@/components/creation-flow/CategoryTagsFields';
import IncludedProductsPicker from '@/components/creation-flow/IncludedProductsPicker';
import { UploadProgress } from '@/components/creation-flow/UploadProgress';

// Intro video (public preview clip) controls — owned by CourseBuilderPage, which
// stages the clip and provisions the Mux asset on save. This card just renders
// state + fires the source/edit callbacks.
export interface IntroVideoProps {
  previewClipS3Key: string | null;
  previewClipName: string | null;
  existingMuxPreview: MuxPreview | null;
  uploading: boolean;
  percent?: number | null;
  editKey: string | null;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRecord: () => void;
  onLibrary: () => void;
  onRemove: () => void;
  onEdit: () => void;
}

interface CourseSettingsProps {
  title: string;
  setTitle: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  price: number;
  setPrice: (v: number) => void;
  pricingMode: 'one_time' | 'subscription';
  setPricingMode: (v: 'one_time' | 'subscription') => void;
  coverImage: string;
  productId?: string;
  category: string;
  setCategory: (v: string) => void;
  tags: string[];
  setTags: (v: string[]) => void;
  plusOfferMonths: number;
  setPlusOfferMonths: (v: number) => void;
  includedProductIds: string[];
  setIncludedProductIds: (v: string[]) => void;
  setCoverImage: (v: string) => void;
  published: boolean;
  setPublished: (v: boolean) => void;
  errors: Record<string, string>;
  introVideo?: IntroVideoProps;
}

const CourseSettings: React.FC<CourseSettingsProps> = ({
  title, setTitle, description, setDescription,
  price, setPrice, pricingMode, setPricingMode,
  coverImage, setCoverImage, published, setPublished, errors, productId,
  category, setCategory, tags, setTags,
  plusOfferMonths, setPlusOfferMonths, introVideo,
  includedProductIds, setIncludedProductIds,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const { uploadUrl, key } = await getProductFileUploadUrl(file.name, file.type);
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      setCoverImage(key);
    } catch {
      // toast handled by parent
    } finally {
      setUploadingCover(false);
    }
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 500,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--hj-beige-ink)',
    fontFamily: 'var(--hj-font-mono)',
    display: 'block',
    marginBottom: 6,
  };
  const errorStyle: React.CSSProperties = {
    marginTop: 4,
    fontSize: 11,
    color: 'hsl(var(--destructive))',
  };

  return (
    <div
      className="flex flex-col gap-5"
      style={{
        padding: 18,
        borderRadius: 'var(--hj-r-md)',
        background: '#fff',
        border: '1px solid var(--hj-beige-line)',
      }}
    >
      <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)' }}>Settings</div>

      {/* Cover Image */}
      <div>
        <Label style={labelStyle}>Cover image</Label>
        <div
          className="w-full aspect-video flex items-center justify-center cursor-pointer transition-colors overflow-hidden relative"
          onClick={() => fileInputRef.current?.click()}
          style={{
            background: 'var(--hj-beige-hover)',
            borderRadius: 'var(--hj-r-sm)',
            border: '1px dashed var(--hj-beige-line)',
          }}
        >
          {coverImage ? (
            <img
              src={coverImage.startsWith('http') || coverImage.startsWith('/api/') ? coverImage : `/api/s3/${coverImage}`}
              alt="Cover"
              className="w-full h-full object-cover"
            />
          ) : uploadingCover ? (
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--hj-beige-ink-3)' }} />
          ) : (
            <div className="flex flex-col items-center gap-1" style={{ color: 'var(--hj-beige-ink-3)' }}>
              <ImageIcon className="w-7 h-7" />
              <span style={{ fontSize: 11.5 }}>Upload cover</span>
            </div>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
        <div style={{ marginTop: 8 }}>
          <MakeInStudioLink output="card" eyebrow="ON-DEMAND COURSE" headline={title || undefined} coverProductId={productId} />
        </div>
      </div>

      {/* Intro video — a short pitch shown on the public course page. Same
          "preview clip" concept video products have: a public Mux asset
          provisioned on save via requestMuxIngest({ kind: 'preview' }). */}
      {introVideo && (
        <div>
          <Label style={labelStyle}>Intro video</Label>
          <p className="m-0" style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-2)', lineHeight: 1.5, marginBottom: 8 }}>
            A short pitch shown on your course page — what students will get. Keep it under ~60 seconds (max 100MB).
          </p>
          <UploadProgress percent={introVideo.uploading ? (introVideo.percent ?? -1) : null} />
          {(introVideo.previewClipS3Key || introVideo.existingMuxPreview) ? (
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center gap-2.5" style={{ padding: '8px 10px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-beige-hover)', border: '1px solid var(--hj-beige-line)' }}>
                <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 30, height: 30, borderRadius: 8, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)' }}><Video className="w-4 h-4" /></span>
                <div className="flex-1 min-w-0">
                  <div className="truncate" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>{introVideo.previewClipName || 'Intro video'}</div>
                  <div style={{ fontSize: 11, color: 'var(--hj-beige-ink-3)' }}>
                    {introVideo.previewClipS3Key
                      ? 'Staged — saves on submit'
                      : `Active (${introVideo.existingMuxPreview?.muxIngestStatus === 'ready' ? 'live' : introVideo.existingMuxPreview?.muxIngestStatus})`}
                  </div>
                </div>
                <button type="button" onClick={introVideo.onRemove} aria-label="Remove intro video" title="Remove" className="flex-shrink-0" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'hsl(var(--destructive))' }}><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="flex flex-wrap gap-2">
                <label className="cursor-pointer inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12, fontWeight: 600, minHeight: 34 }}>
                  {introVideo.uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Replace
                  <input type="file" accept="video/*" className="hidden" onChange={introVideo.onUpload} disabled={introVideo.uploading} />
                </label>
                {!!introVideo.editKey && (
                  <button type="button" onClick={introVideo.onEdit} className="inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12, fontWeight: 600, minHeight: 34, cursor: 'pointer' }}>
                    <Video className="w-3.5 h-3.5" /> Edit video
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer">
                <span className="inline-flex items-center gap-1.5 w-full justify-center" style={{ padding: '8px 14px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 600, minHeight: 38 }}>
                  {introVideo.uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload a clip
                </span>
                <input type="file" accept="video/*" className="hidden" onChange={introVideo.onUpload} disabled={introVideo.uploading} />
              </label>
              <div className="flex gap-2">
                <button type="button" onClick={introVideo.onRecord} disabled={introVideo.uploading} className="flex-1 inline-flex items-center justify-center gap-1.5" style={{ padding: '8px 12px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12.5, fontWeight: 600, minHeight: 38, cursor: 'pointer' }}>
                  <Video className="w-4 h-4" /> Record
                </button>
                <button type="button" onClick={introVideo.onLibrary} disabled={introVideo.uploading} className="flex-1 inline-flex items-center justify-center gap-1.5" style={{ padding: '8px 12px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12.5, fontWeight: 600, minHeight: 38, cursor: 'pointer' }}>
                  <FolderOpen className="w-4 h-4" /> Library
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Title */}
      <div>
        <Label htmlFor="course-title" style={labelStyle}>Course title</Label>
        <Input
          id="course-title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Mastering self-tapes"
        />
        {errors.title && <p style={errorStyle}>{errors.title}</p>}
      </div>

      {/* Description */}
      <div>
        <Label htmlFor="course-desc" style={labelStyle}>Description</Label>
        <Textarea
          id="course-desc"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="What will students learn?"
          rows={4}
        />
      </div>

      <CategoryTagsFields category={category} setCategory={setCategory} tags={tags} setTags={setTags} />

      {/* Membership bundle offer — renders on the public class page */}
      <div>
        <Label htmlFor="course-plus-offer" style={labelStyle}>Hey Joe Plus offer (free months)</Label>
        <Input
          id="course-plus-offer"
          type="number"
          min={0}
          max={24}
          value={plusOfferMonths || ''}
          onChange={e => setPlusOfferMonths(Math.max(0, Math.min(24, parseInt(e.target.value, 10) || 0)))}
          placeholder="0 = no offer"
        />
        <p style={{ marginTop: 4, fontSize: 11, color: 'var(--hj-beige-ink-3)' }}>
          Buyers see "includes N months of Hey Joe Plus free" on the class page and redeem with the promo code after checkout.
        </p>
      </div>

      {/* Pricing Mode */}
      <div>
        <Label style={labelStyle}>Pricing</Label>
        <Select value={pricingMode} onValueChange={(v: 'one_time' | 'subscription') => setPricingMode(v)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="one_time">One-time purchase</SelectItem>
            <SelectItem value="subscription">Monthly subscription</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Price */}
      <div>
        <Label htmlFor="course-price" style={labelStyle}>
          Price (USD){pricingMode === 'subscription' ? ' / month' : ''}
        </Label>
        <div className="relative">
          <span
            className="absolute"
            style={{
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--hj-beige-ink-3)',
              fontSize: 14,
              fontFamily: 'var(--hj-font-mono)',
            }}
          >
            $
          </span>
          <Input
            id="course-price"
            type="number"
            min={0}
            step={0.01}
            value={(price / 100).toFixed(2)}
            onChange={e => setPrice(Math.round(parseFloat(e.target.value || '0') * 100))}
            className="pl-7"
          />
        </div>
        {errors.price && <p style={errorStyle}>{errors.price}</p>}
        {pricingMode === 'subscription' && price === 0 && !errors.price && (
          <p style={{ marginTop: 6, fontSize: 11.5, color: 'var(--hj-beige-ink-3)' }}>
            Free membership. Anyone can join at no charge, and members get everything you include below.
          </p>
        )}
      </div>

      {pricingMode === 'subscription' && (
        <IncludedProductsPicker value={includedProductIds} onChange={setIncludedProductIds} excludeId={productId} />
      )}

      {/* Published Toggle */}
      <div
        className="flex items-center justify-between"
        style={{
          paddingTop: 12,
          borderTop: '1px solid var(--hj-beige-line)',
        }}
      >
        <div>
          <Label style={{ fontSize: 13, color: 'var(--hj-beige-ink)', fontWeight: 500 }}>Published</Label>
          <p style={{ marginTop: 2, fontSize: 11.5, color: 'var(--hj-beige-ink-3)' }}>
            {published ? 'Visible in your store.' : 'Hidden — drafts only.'}
          </p>
        </div>
        <Switch checked={published} onCheckedChange={setPublished} />
      </div>
    </div>
  );
};

export default CourseSettings;
